import type {
  HttpError,
  HttpRequestAbstract,
  HttpRequestConfig,
  HttpResponse,
} from 'uview-plus/libs/luch-request/index';
import { AuthHelper } from '@/utils/auth/helper';
import { showMessage } from './status';

function requestInterceptors(http: HttpRequestAbstract) {
  /**
   * 请求拦截 - 智能Token管理
   * @param {object} http
   */
  http.interceptors.request.use(
    async (config: HttpRequestConfig) => {
      // 初始化请求拦截器时，会执行此方法，此时data为undefined，赋予默认{}
      config.data = config.data || {};
      // 自定义参数
      const custom = config?.custom;

      // 智能Token设置
      const isToken = custom?.auth === false;
      if (!isToken && config.header) {
        try {
          const token = await AuthHelper.getValidToken();
          if (token) {
            config.header.Authorization = `Bearer ${token}`;
          }
          else {
            // 无有效token，删除Authorization头，让401拦截器处理
            delete config.header.Authorization;
          }
        }
        catch (error) {
          // token刷新失败，删除Authorization头，让401拦截器处理
          console.warn('Token获取失败，将由401拦截器处理重新认证:', error);
          delete config.header.Authorization;
        }
      }

      // 是否显示 loading
      if (custom?.loading) {
        uni.showLoading({
          title: '加载中',
          mask: true,
        });
      }

      return config;
    },
    (config: any) => // 可使用async await 做异步操作
      Promise.reject(config),
  );
}
function responseInterceptors(http: HttpRequestAbstract) {
  /**
   * 响应拦截
   * @param {object} http
   */
  http.interceptors.response.use((response: HttpResponse) => {
    /* 对响应成功做点什么 可使用async await 做异步操作 */
    const data = response.data;
    // 配置参数
    const config = response.config;
    // 自定义参数
    const custom = config?.custom;

    // 用户被拉黑 - 立即踢出系统
    if (data.code === 40003) {
      AuthHelper.handleUserBlocked();
      return Promise.reject(data);
    }

    // 登录状态失效 - 委托给AuthHelper处理
    if (data.code === 401) {
      return AuthHelper.handle401(config, http);
    }

    // Token刷新冲突 - 智能重试（检查token是否已被其他请求更新）
    if (data.code === 409) {
      const retryCount = (config as any)._retryCount || 0;
      if (retryCount === 0) {
        return new Promise((resolve) => {
          setTimeout(async () => {
            try {
              // 重试前检查token是否已被其他请求刷新
              const currentToken = await AuthHelper.getValidToken();
              if (currentToken && config.header) {
                // token已更新，直接用新token重试原请求
                config.header.Authorization = `Bearer ${currentToken}`;
              }
            }
            catch {
              // token获取失败，继续正常重试流程
            }

            (config as any)._retryCount = 1;
            resolve(http.request(config));
          }, 500);
        });
      }
    }

    // 隐藏loading
    if (custom?.loading) {
      uni.hideLoading();
    }

    // 请求成功则返回结果
    if (data.code === 200) {
      return response || {};
    }

    // 用户未注册 - 让调用方处理（不toast）
    if (data.code === 40001) {
      return Promise.reject(data);
    }

    // 如果没有显式定义custom的toast参数为false的话，默认对报错进行toast弹出提示
    if (custom?.toast !== false) {
      uni.$u.toast(data.message);
    }

    // 请求失败则抛出错误
    return Promise.reject(data);
  }, (response: HttpError) => {
    // 自定义参数
    const custom = response.config?.custom;

    // 隐藏loading
    if (custom?.loading !== false) {
      uni.hideLoading();
    }

    // 如果没有显式定义custom的toast参数为false的话，默认对报错进行toast弹出提示
    if (custom?.toast !== false) {
      const message = response.statusCode ? showMessage(response.statusCode) : '网络异常，请稍后再试';
      uni.$u.toast(message);
    }

    return Promise.reject(response);
  });
}

export { requestInterceptors, responseInterceptors };
